import type { SheetRow } from "@vocifuchs/shared";

/**
 * The print sheet as a PDF, made in the browser for the one place where
 * `window.print()` does nothing: a web app started from the iOS home screen.
 * The share sheet then offers «Drucken» and «In Dateien sichern». Same layout
 * as the printed page (docs/UX.md §2): German word left, a line to write on
 * right, about 12 mm a row, the solution from a page of its own.
 *
 * jsPDF is loaded only here, so nobody else downloads it. Its built-in
 * Helvetica covers German, English and French (é, ç, œ, «»), not «→».
 */

export interface SheetPdf {
  title: string;
  /** Under the title, on the sheet and on the solution. */
  heading: string;
  solutionTitle: string;
  footer: string;
  /** Between two accepted answers on the solution page. */
  or: string;
  rows: SheetRow[];
}

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - 2 * MARGIN;
const GAP = 6;
/** 2 : 3, as on screen. */
const WORD_W = ((CONTENT_W - GAP) * 2) / 5;
const LINE_X = MARGIN + WORD_W + GAP;
const ROW_MIN = 12;
const TEXT_PT = 12;
/** A long compound shrinks to fit its column, down to this, before it breaks. */
const TEXT_PT_MIN = 9;
const PT_MM = 25.4 / 72;
/** Room at the bottom of each page for the footer. */
const BOTTOM = PAGE_H - MARGIN - 6;

/** WinAnsi beyond Latin-1: what Helvetica in jsPDF writes as single bytes. */
const WIN_ANSI_EXTRA = new Set("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ");
const winAnsi = (c: string) => {
  const code = c.codePointAt(0)!;
  return (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || WIN_ANSI_EXTRA.has(c);
};

/**
 * Text the PDF font can write. One character outside WinAnsi makes jsPDF
 * write the whole string as two-byte junk, and textguard allows more (any
 * Latin letter, the dashes U+2010–2015, minus): a scan can bring in «‐».
 * Such a character loses its accent, or becomes its plain look-alike.
 */
export function pdfText(text: string): string {
  return Array.from(text, (c) => {
    if (winAnsi(c)) return c;
    if (/[\u2010-\u2012\u2212]/.test(c)) return "-";
    if (c === "ẞ") return "SS";
    const base = c.normalize("NFD").replace(/\p{Mark}/gu, "");
    return Array.from(base).every(winAnsi) ? base : "?";
  }).join("");
}

export async function sheetPdf(sheet: SheetPdf): Promise<File> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setProperties({ title: sheet.title });
  const lineH = (size: number) => size * PT_MM * doc.getLineHeightFactor();

  const footer = () => {
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(0);
    doc.text(sheet.footer, MARGIN, PAGE_H - 10);
  };

  /** Title and heading at the top of a page; returns where the rows start. */
  const header = (title: string): number => {
    doc.setFont("helvetica", "bold").setFontSize(22).setTextColor(0);
    const lines: string[] = doc.splitTextToSize(title, CONTENT_W);
    let y = MARGIN + lineH(22) * 0.8;
    doc.text(lines, MARGIN, y);
    y += lineH(22) * (lines.length - 1) + lineH(11) + 1;
    doc.setFont("helvetica", "normal").setFontSize(11);
    doc.text(pdfText(sheet.heading), MARGIN, y);
    return y + 8;
  };

  /**
   * The size at which the longest single word of `text` fits `width`: jsPDF
   * breaks a word that does not fit anywhere, without a hyphen.
   */
  const fit = (text: string, width: number): number => {
    doc.setFontSize(TEXT_PT);
    const widest = Math.max(...text.split(/\s+/).map((w) => doc.getTextWidth(w)));
    // A hair below the exact fit: at the exact size jsPDF rounds the other
    // way and moves the last letter to a line of its own.
    return Math.max(TEXT_PT_MIN, Math.min(TEXT_PT, (TEXT_PT * width * 0.99) / widest));
  };

  const page = (title: string, solution: boolean) => {
    let y = header(pdfText(title));
    for (const row of sheet.rows) {
      const prompt = pdfText(row.prompt);
      doc.setFont("helvetica", "bold");
      const wordPt = fit(prompt, WORD_W);
      const word: string[] = doc.setFontSize(wordPt).splitTextToSize(prompt, WORD_W);
      doc.setFont("helvetica", "normal");
      const answerText = pdfText(row.answers.join(` ${sheet.or} `));
      const answerPt = solution ? fit(answerText, CONTENT_W - WORD_W - GAP) : TEXT_PT;
      const answer: string[] = solution
        ? doc.setFontSize(answerPt).splitTextToSize(answerText, CONTENT_W - WORD_W - GAP)
        : [];
      const height = Math.max(
        ROW_MIN,
        word.length * lineH(wordPt) + 5,
        answer.length * lineH(answerPt) + 5,
      );
      // A row never runs across a page break.
      if (y + height > BOTTOM) {
        footer();
        doc.addPage();
        y = MARGIN;
      }
      const bottom = y + height;
      // Bottom-aligned, so the word sits right above its line.
      const baseline = (n: number, pt: number) => bottom - 2.5 - (n - 1) * lineH(pt);
      doc.setFont("helvetica", "bold").setFontSize(wordPt);
      doc.text(word, MARGIN, baseline(word.length, wordPt));
      if (solution) {
        doc.setFont("helvetica", "normal").setFontSize(answerPt);
        doc.text(answer, LINE_X, baseline(answer.length, answerPt));
      } else {
        doc.setLineWidth(0.4).setDrawColor(0);
        doc.line(LINE_X, bottom - 1.5, PAGE_W - MARGIN, bottom - 1.5);
      }
      y = bottom;
    }
    footer();
  };

  page(sheet.title, false);
  doc.addPage();
  page(sheet.solutionTitle, true);

  const name = sheet.title.replace(/[\\/:*?"<>|]+/g, " ").trim() || "VociFuchs";
  return new File([doc.output("blob")], `${name}.pdf`, { type: "application/pdf" });
}
